"use client";
import Link from "next/link";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/query-keys";
import { useProfile } from "@/contexts/ProfileContext";
import { isCommissionBanned } from "@/lib/commission-standing";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserBadges } from "@/components/ui/user-badges";
import { SocialLinks } from "@/components/ui/social-links";
import { SellerTrustScore } from "@/components/ui/seller-trust-score";
import { SubscriptionBadge } from "@/components/ui/subscription-badge";
import { useSellerTrustScore } from "@/hooks/useSellerTrustScore";
import { useUserBadges } from "@/hooks/useUserBadges";
import { usePublicTier } from "@/hooks/usePublicTier";
import { useUserFollowing, useFollowSeller, useUnfollowSeller } from "@/hooks/useUserFollowing";
import { MessageSellerButton } from "@/components/messages/MessageSellerButton";
import { ResponseTimeBadge } from "@/components/messages/ChatBits";
import { HireCreatorDialog } from "@/components/commissions/HireCreatorDialog";
import { useAuthModal } from "@/contexts/AuthModalContext";
import Briefcase from "lucide-react/dist/esm/icons/briefcase";
import DollarSign from "lucide-react/dist/esm/icons/dollar-sign";
import { logEvent } from "@/lib/events";
import type { AssetDetail } from "@/hooks/useAssetDetail";

interface SellerCardProps {
  seller: AssetDetail["seller"];
  /** The listing being viewed, so a message to the seller says what it's about. */
  asset?: { id: string; title: string } | null;
}

export function SellerCard({ seller, asset }: SellerCardProps) {
  const { profile: currentUserProfile } = useProfile();
  const { openAuthModal } = useAuthModal();
  const { data: trustScore } = useSellerTrustScore(seller?.id ?? "");
  const { data: roles } = useUserBadges(seller?.user_id ?? "");
  const { data: sellerTier } = usePublicTier(seller?.id);
  const followSeller = useFollowSeller();
  const unfollowSeller = useUnfollowSeller();
  const { data: following } = useUserFollowing();
  const [hireMeOpen, setHireMeOpen] = useState(false);

  // Fetch hire availability from profiles table
  const { data: hireInfo } = useQuery({
    queryKey: queryKeys.sellerDashboard.hireInfo(seller?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("public_profiles")
        .select("hireable, hire_starting_price, hire_min_budget, hire_specialties")
        .eq("id", seller!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!seller?.id,
  });

  if (!seller) return null;

  const displayName = seller.display_name || seller.username || "Anonymous Seller";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const profileUrl = `/store/${seller.username || seller.id}`;
  const isOwnProfile = currentUserProfile?.id === seller.id;
  const viewerCommissionBanned = isCommissionBanned(currentUserProfile);
  const isFollowing = following?.some((f) => f.seller.id === seller.id) ?? false;

  return (
    <>
      <Card className="group transition-colors hover:border-primary/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">About the Seller</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Link
              href={profileUrl}
              className="flex items-center gap-3 flex-1 min-w-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Avatar className="h-12 w-12">
                <AvatarImage src={seller.avatar_url || undefined} alt={displayName} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold truncate group-hover:text-primary transition-colors">{displayName}</span>
                  {sellerTier && sellerTier !== "free" && (
                    <SubscriptionBadge tier={sellerTier} size="sm" />
                  )}
                  <UserBadges
                    user={{
                      is_seller: true,
                      seller_verified: seller.seller_verified,
                      is_early_founder: (seller as any).is_early_founder,
                      roles: roles || [],
                    }}
                    size="sm"
                  />
                </div>
                {seller.username && (
                  <p className="text-sm text-muted-foreground">@{seller.username}</p>
                )}
              </div>
            </Link>
            {trustScore && (
              <SellerTrustScore result={trustScore} size="sm" showLabel={false} />
            )}
          </div>

          {seller.bio && (
            <p className="text-sm text-muted-foreground line-clamp-3">
              {seller.bio}
            </p>
          )}

          {/* Available for Hire */}
          {hireInfo?.hireable && !viewerCommissionBanned && !isOwnProfile && (
            <button
              type="button"
              className="w-full p-4 rounded-xl bg-primary/5 border border-primary/30 text-left space-y-2 hover:bg-primary/10 hover:border-primary/50 transition-colors duration-200 group/hire"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!currentUserProfile) {
                  // Keep the hire intent through sign-up — the dialog opens
                  // the moment they're in (same as the creator directory).
                  openAuthModal({
                    message: `Create an account to hire ${displayName}`,
                    onSuccess: () => setHireMeOpen(true),
                  });
                  return;
                }
                setHireMeOpen(true);
              }}
            >
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-primary/20 dark:bg-primary/30">
                  <Briefcase className="h-4 w-4 text-primary" />
                </div>
                <span>Available for Hire</span>
                <span className="ml-auto text-xs font-medium text-primary opacity-0 group-hover/hire:opacity-100 transition-opacity duration-300">
                  Hire →
                </span>
              </div>
              {hireInfo.hire_specialties && hireInfo.hire_specialties.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {hireInfo.hire_specialties.slice(0, 3).map((tag, i) => (
                    <span key={i} className="inline-flex items-center rounded-full bg-primary/10 border border-primary/20 px-2 py-0.5 text-2xs font-medium text-primary">
                      {tag}
                    </span>
                  ))}
                  {hireInfo.hire_specialties.length > 3 && (
                    <span className="text-2xs text-muted-foreground self-center">+{hireInfo.hire_specialties.length - 3}</span>
                  )}
                </div>
              )}
              {(hireInfo.hire_min_budget ?? hireInfo.hire_starting_price) && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  From ${hireInfo.hire_min_budget ?? hireInfo.hire_starting_price}
                </p>
              )}
            </button>
          )}

          <SocialLinks
            links={{
              social_youtube: seller.social_youtube,
              social_twitter: seller.social_twitter,
              social_discord: seller.social_discord,
              social_tiktok: seller.social_tiktok,
              social_instagram: seller.social_instagram,
            }}
            size="sm"
          />

          {/* Follow / Unfollow button */}
          {!isOwnProfile && (
            <Button
              variant={isFollowing ? "outline" : "default"}
              size="sm"
              className="w-full"
              aria-pressed={isFollowing}
              aria-label={isFollowing ? `Unfollow ${displayName}` : `Follow ${displayName}`}
              disabled={followSeller.isPending || unfollowSeller.isPending}
              onClick={() => {
                if (!currentUserProfile) {
                  openAuthModal({
                    message: "Follow this creator",
                    intent: { kind: "follow", sellerProfileId: seller.id, sellerName: displayName },
                    context: { title: displayName, thumbnail: seller.avatar_url },
                  });
                  return;
                }
                logEvent("seller_follow_clicked", { seller_id: seller.id }, { severity: "medium" });
                if (isFollowing) {
                  unfollowSeller.mutate(seller.id);
                } else {
                  followSeller.mutate(seller.id);
                }
              }}
            >
              {isFollowing ? "Following" : "Follow"}
            </Button>
          )}

          {!isOwnProfile && (
            <>
              <MessageSellerButton
                recipientId={seller.id}
                recipientName={displayName}
                contextType={asset ? "asset" : "store"}
                contextId={asset?.id ?? null}
                contextLabel={asset?.title ?? null}
                label="Message seller"
                placeholder={asset ? `Hi ${displayName}, a question about ${asset.title}: …` : undefined}
                size="sm"
                className="w-full"
              />
              <ResponseTimeBadge profileId={seller.id} className="justify-center" />
            </>
          )}

          <Link
            href={profileUrl}
            className="inline-flex items-center justify-center w-full rounded-md border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm font-semibold text-primary hover:bg-primary/10 transition-colors"
            onClick={() => logEvent("seller_store_visited_from_asset", { seller_id: seller.id }, { severity: "low" })}
          >
            View profile
          </Link>
        </CardContent>
      </Card>

      {/* Hire Modal */}
      {hireMeOpen && (
        <HireCreatorDialog
          open={hireMeOpen}
          onOpenChange={setHireMeOpen}
          sellerId={seller.id}
          sellerName={displayName}
          startingPrice={hireInfo?.hire_starting_price}
          minBudget={hireInfo?.hire_min_budget}
        />
      )}
    </>
  );
}
